import { useEffect, useRef, useState } from 'react';
import { Paragraph, View, XStack, YStack } from 'tamagui';

import { Text } from '@/components/text';
import { SolidSurface } from '@/components/glass-surface';
import { LoadingIndicator } from '@/components/loading-indicator';
import { PrimaryButton } from '@/components/primary-button';
import { TextLink } from '@/components/text-link';
import type { useCurrentLocation } from '@/hooks/use-current-location';
import { space } from '@/theme/tokens.generated';

// Issue #150 Task 6. Previously existed twice (profile/index.tsx,
// create-thread/index.tsx) -- issue #150's own AC assumed "its privacy
// claim is identical everywhere it renders," but reading both copies
// before unifying (per this task's own instruction) found that was
// NOT true: profile's idle-state copy included the actual privacy
// assurance ("it's never shown to other users, only used to match you
// within your radius"); create-thread's idle-state copy
// ("Attach your current location so people nearby can find this
// thread") omitted it entirely. That's a real gap, not a cosmetic one
// -- a user granting location from the create-thread flow never saw
// the same reassurance a user granting it from the profile flow did.
// Fixed here, not just deduped: the privacy clause below is now FIXED
// (not parametrized), so every caller gets it, and only the
// screen-specific "what this is for" noun phrase varies via `context`.
//
// The "granted" row's Update link (profile: shown; create-thread:
// previously omitted, "re-requesting mid-draft is the wrong default
// here" per create-thread's own prior comment) stays a real,
// screen-owned decision -- `showUpdateLink` prop, not unified away,
// since that one IS a legitimate behavioral difference, not copy
// drift. "Location attached"/"Location set" wording, which carried no
// distinct meaning, IS unified to one string ("Location set").
export interface LocationCardProps {
  location: ReturnType<typeof useCurrentLocation>;
  // The specific reason THIS screen wants location -- e.g. "post this
  // thread nearby" (create-thread) or "show you what's happening
  // nearby" (profile). Used in both the denied-state ask and the
  // idle-state explanation, so the two states stay consistent with
  // each other within one screen too, not just across screens.
  context: string;
  showUpdateLink?: boolean;
}

export function LocationCard({ location, context, showUpdateLink = false }: LocationCardProps) {
  const { phase, requestLocation, openSettings, canOpenSettings } = location;

  // Re-requesting while already 'granted' (the Update link) round-trips
  // through the exact same 'locating' phase a first-time grant does, but
  // on a device with a fast/cached GPS fix that phase can resolve in well
  // under a render frame -- founder-reported: tapping Update produced "no
  // valuable visual feedback" and felt like it needed repeated presses,
  // even though a fresh fetch genuinely ran every time. This tracks
  // whether the CURRENT 'granted' state was just reached via a re-request
  // (not the screen's very first grant) and holds a distinct "Location
  // updated" confirmation on screen for a fixed window, so a fast
  // response is never invisible.
  const wasLocating = useRef(false);
  const [justUpdated, setJustUpdated] = useState(false);

  useEffect(() => {
    if (phase.phase === 'locating') {
      wasLocating.current = true;
      return;
    }

    if (phase.phase === 'granted' && wasLocating.current) {
      wasLocating.current = false;
      setJustUpdated(true);
      const timeout = setTimeout(() => setJustUpdated(false), 2000);
      return () => clearTimeout(timeout);
    }
  }, [phase.phase]);

  return (
    <SolidSurface style={{ padding: space[4] }}>
      <YStack gap="$3">
        {phase.phase === 'granted' ? (
          <XStack alignItems="center" gap="$2">
            <View width={8} height={8} borderRadius="$full" backgroundColor="$primary" />
            <Text
              color={justUpdated ? '$feedbackSuccessText' : '$color'}
              flex={1}
              fontWeight={justUpdated ? '600' : undefined}
            >
              {justUpdated ? 'Location updated' : 'Location set'}
            </Text>
            {showUpdateLink && (
              <TextLink fontSize="$meta" lineHeight="$meta" onPress={() => requestLocation()}>
                Update
              </TextLink>
            )}
          </XStack>
        ) : phase.phase === 'locating' ? (
          <XStack alignItems="center" gap="$3">
            <LoadingIndicator label="Getting your location" />
            <Text color="$colorSecondary">Getting your location...</Text>
          </XStack>
        ) : phase.phase === 'denied' ? (
          <YStack gap="$3">
            <Paragraph color="$colorSecondary">
              Nearkin needs your location to {context} — enable it in Settings.
            </Paragraph>
            <PrimaryButton onPress={canOpenSettings ? openSettings : () => requestLocation()}>
              {canOpenSettings ? 'Open Settings' : 'Try again'}
            </PrimaryButton>
            {canOpenSettings && (
              <TextLink textAlign="center" onPress={() => requestLocation()}>
                Try again
              </TextLink>
            )}
          </YStack>
        ) : phase.phase === 'error' ? (
          <YStack gap="$3">
            <Paragraph color="$destructive">{phase.message}</Paragraph>
            <PrimaryButton onPress={() => requestLocation()}>Try again</PrimaryButton>
          </YStack>
        ) : (
          <YStack gap="$3">
            <Paragraph color="$colorSecondary">
              Nearkin uses your location to {context}. It&apos;s never shown to other users, only used to match you
              within your radius.
            </Paragraph>
            <PrimaryButton onPress={() => requestLocation()}>Share location</PrimaryButton>
          </YStack>
        )}
      </YStack>
    </SolidSurface>
  );
}
